import React, { useRef, useState } from "react";
import { Printer, FileText, Download, Bell } from "lucide-react";
import {
  getReceiptHtmlUrl,
  getReceiptHtmlUrlPortal,
  getReceiptPdfUrl,
  getReceiptPdfUrlPortal,
  sendReceiptNotification,
  sendReceiptNotificationPortal,
} from "../../api/fees/feeApi";
import { Button, Alert, Badge, Table } from "../../components/ui";

const FeeReceiptModal = ({ isOpen, onClose, receipt, isPortal = false }) => {
  const printRef = useRef(null);
  const [notifyMsg, setNotifyMsg] = useState("");
  const [notifyLoading, setNotifyLoading] = useState(false);

  if (!isOpen || !receipt) return null;

  const student = receipt.studentId || {};
  const name =
    typeof student === "object"
      ? [student.firstName, student.middleName, student.lastName]
          .filter(Boolean)
          .join(" ") ||
        student.name ||
        "—"
      : "—";

  const handlePrintLocal = () => {
    const content = printRef.current;
    if (!content) return;
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>Receipt ${receipt.receiptNo}</title>
      <style>
        body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;padding:24px;color:#111827}
        table{width:100%;border-collapse:collapse;margin-top:16px}
        th,td{border:1px solid #E5E7EB;padding:10px 12px;font-size:13px;text-align:left}
        th{background:#F9FAFB;font-weight:600;color:#374151}
        .right{text-align:right}
        .header{text-align:center;margin-bottom:24px}
        .meta-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;font-size:13px;margin-bottom:20px}
        .voided{color:#DC2626;font-weight:bold}
      </style></head><body>${content.innerHTML}</body></html>`);
    w.document.close();
    w.focus();
    w.print();
  };

  const handleOpenServerHtml = () => {
    const id = receipt._id;
    if (!id) return handlePrintLocal();
    const url = isPortal ? getReceiptHtmlUrlPortal(id) : getReceiptHtmlUrl(id);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleDownloadPdf = () => {
    const id = receipt._id;
    if (!id) return;
    const url = isPortal ? getReceiptPdfUrlPortal(id) : getReceiptPdfUrl(id);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleNotify = async () => {
    if (!receipt._id) return;
    setNotifyLoading(true);
    setNotifyMsg("");
    try {
      const fn = isPortal
        ? sendReceiptNotificationPortal
        : sendReceiptNotification;
      await fn({ receiptId: receipt._id });
      setNotifyMsg("Notification queued / sent (provider-dependent).");
    } catch (err) {
      setNotifyMsg(err.response?.data?.message || "Notification failed");
    } finally {
      setNotifyLoading(false);
    }
  };

  const breakdown = receipt.paymentBreakdown || [];

  const columns = [
    {
      key: "particulars",
      header: "Particulars",
      render: (row) => (
        <span className="font-medium text-neutral-800 dark:text-neutral-100">
          {row.feeHeadName || row.title || "Fee"}
        </span>
      ),
    },
    {
      key: "amount",
      header: "Amount",
      align: "right",
      render: (row) => (
        <span className="font-medium text-neutral-900 dark:text-neutral-100">
          ₹{Number(row.amountAdjusted || row.amount || 0).toFixed(2)}
        </span>
      ),
    },
  ];

  const tableData =
    breakdown.length === 0
      ? [{ feeHeadName: "Payment", amountAdjusted: receipt.totalPaid }]
      : breakdown;

  return (
    <div className="fixed inset-0 top-0 left-0 right-0 bottom-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 sm:p-6 overflow-y-auto">
      <div className="relative my-8 sm:my-12 w-full max-w-2xl bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
        {/* Modal Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between px-6 py-4 border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 gap-3 shrink-0">
          <h2 className="text-lg font-bold text-neutral-900 dark:text-neutral-100">Fee Receipt</h2>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline-primary"
              size="xs"
              onClick={handlePrintLocal}
              leftIcon={<Printer className="h-3.5 w-3.5" />}
            >
              Print
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="xs"
              onClick={handleOpenServerHtml}
              leftIcon={<FileText className="h-3.5 w-3.5" />}
            >
              Open HTML
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="xs"
              onClick={handleDownloadPdf}
              leftIcon={<Download className="h-3.5 w-3.5" />}
            >
              PDF
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="xs"
              isLoading={notifyLoading}
              onClick={handleNotify}
              leftIcon={<Bell className="h-3.5 w-3.5" />}
            >
              Notify
            </Button>
            <button
              type="button"
              onClick={onClose}
              className="text-neutral-400 dark:text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-200 text-2xl font-semibold leading-none ml-2"
            >
              &times;
            </button>
          </div>
        </div>

        {/* Scrollable Receipt Body */}
        <div className="p-6 overflow-y-auto space-y-4 flex-1">
          {notifyMsg && (
            <Alert variant="info" title="Notification Status">
              {notifyMsg}
            </Alert>
          )}

          <div
            ref={printRef}
            className="border border-neutral-200 dark:border-neutral-800 rounded-xl p-5 sm:p-6 bg-white dark:bg-neutral-900 space-y-6"
          >
            {/* Printable Title Header */}
            <div className="text-center pb-4 border-b border-neutral-100 dark:border-neutral-800">
              <h1 className="text-xl font-bold text-neutral-900 dark:text-neutral-100">Fee Receipt</h1>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
                Receipt No:{" "}
                <span className="font-semibold text-neutral-800 dark:text-neutral-100">
                  {receipt.receiptNo}
                </span>
              </p>
              <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5">
                Date:{" "}
                {receipt.paymentDate
                  ? new Date(receipt.paymentDate).toLocaleDateString("en-IN")
                  : "—"}
              </p>
              {receipt.isVoided && (
                <div className="mt-2">
                  <Badge variant="danger" size="lg">
                    VOIDED
                  </Badge>
                </div>
              )}
            </div>

            {/* Student & Payment Summary Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs sm:text-sm bg-neutral-50 dark:bg-neutral-950 p-4 rounded-xl border border-neutral-100 dark:border-neutral-800">
              <div>
                <p className="text-neutral-500 dark:text-neutral-400 text-xs font-medium">Student</p>
                <p className="font-semibold text-neutral-800 dark:text-neutral-100 mt-0.5">{name}</p>
              </div>
              <div>
                <p className="text-neutral-500 dark:text-neutral-400 text-xs font-medium">
                  Admission No
                </p>
                <p className="font-semibold text-neutral-800 dark:text-neutral-100 mt-0.5">
                  {student.admissionNo || "—"}
                </p>
              </div>
              <div>
                <p className="text-neutral-500 dark:text-neutral-400 text-xs font-medium">
                  Payment Mode
                </p>
                <p className="font-semibold text-neutral-800 dark:text-neutral-100 mt-0.5">
                  {receipt.paymentMode || "—"}
                </p>
              </div>
              <div>
                <p className="text-neutral-500 dark:text-neutral-400 text-xs font-medium">
                  Transaction ID
                </p>
                <p className="font-semibold text-neutral-800 dark:text-neutral-100 mt-0.5">
                  {receipt.transactionId || "—"}
                </p>
              </div>
            </div>

            {/* Particulars Breakdown */}
            <div className="border border-neutral-200 dark:border-neutral-800 rounded-xl overflow-hidden">
              <Table columns={columns} data={tableData} />
            </div>

            {/* Total and Remarks */}
            <div className="space-y-2 pt-2 border-t border-neutral-100 dark:border-neutral-800">
              <div className="flex justify-between items-center text-base font-bold text-neutral-900 dark:text-neutral-100">
                <span>Total Paid:</span>
                <span className="text-primary-900 dark:text-primary-300">
                  ₹{Number(receipt.totalPaid || 0).toFixed(2)}
                </span>
              </div>
              {receipt.remarks && (
                <p className="text-xs text-neutral-500 dark:text-neutral-400 italic">
                  Remarks: {receipt.remarks}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-3 border-t border-neutral-100 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-950 flex justify-end shrink-0">
          <Button type="button" variant="secondary" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FeeReceiptModal;